import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import SmartOnboardingWizard from "../components/SmartOnboardingWizard";
import { Sparkles, Minus } from "lucide-react";

export default function OnboardingOverlay() {
  const { user, updateUser, api } = useAuth();
  const [minimized, setMinimized] = useState(false);

  if (!user || user.has_seen_onboarding !== false) return null;
  if (user.must_reset_password) return null; // Password reset takes priority
  // Platform admin / webadmin never sees tenant onboarding wizard
  if (user.role === "admin" || user.is_platform_admin) return null;

  const handleComplete = async () => {
    try {
      await api.post("/auth/onboarding-dismiss");
    } catch (e) {
      // Silent fail — dismiss flag is best-effort
    }
    updateUser({ has_seen_onboarding: true });
  };

  // When minimized, show a small floating launcher so the user can finish
  // onboarding later. The server-side `has_seen_onboarding` flag stays
  // `false` so the wizard returns next login if they never finish.
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-20 right-6 z-[90] flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 px-5 py-3 text-sm font-semibold text-white shadow-xl shadow-sky-500/30 hover:shadow-2xl hover:scale-105 transition-all"
        data-testid="onboarding-resume-btn"
      >
        <Sparkles className="w-4 h-4" />
        Finish Setup
        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-white/25 px-1.5 text-[10px] font-bold">
          4 steps
        </span>
      </button>
    );
  }

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto p-0"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
        data-testid="onboarding-overlay"
      >
        <DialogHeader className="px-6 pt-6 pb-0">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Sparkles className="w-5 h-5 text-sky-500" />
                Welcome to ResiliencyHUB
              </DialogTitle>
              <DialogDescription>
                Let's set up your organization in a few quick steps. This helps us tailor the platform to your industry and structure.
              </DialogDescription>
            </div>
            <button
              type="button"
              onClick={() => setMinimized(true)}
              className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-900 transition-colors"
              title="Minimize — you can finish this later"
              data-testid="onboarding-minimize-btn"
            >
              <Minus className="w-3.5 h-3.5" />
              Minimize
            </button>
          </div>
        </DialogHeader>
        <div className="px-6 pb-6">
          <SmartOnboardingWizard
            onComplete={handleComplete}
            initialData={{ name: user.org_name || "" }}
            mode="full"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
